// The *Unassigned faces* tab, connected (PRD-0031 slice 04).
//
// The page, the selection and the two writes are the shared selection hook's;
// the grid, the create dialog and the assign picker under `components/` stay
// pure (ADR-0039). Both writes go through the API's manual-identity endpoints,
// which confirm the faces with the PRD-0030 decision service, so a face already
// confirmed elsewhere comes back as a 409 the tab surfaces as it is.
//
// A recording's passes come as one card per cluster (PRD-0074 slice 04): the
// analyst ticks the card, and a write confirms every face the card stands for —
// one request, one decision per face on the server.
import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import {
  invalidateFaceMatching,
  useFaceReadId,
} from '@/features/face-matching/hooks/useFaceMatching'
import {
  identitiesApi,
  type ManualIdentity,
  type UnassignedFace,
  type UnassignedFacesPage,
} from '@/services/api/identities'
import {
  type ManualIdentityNames,
  type UnassignedSelection,
  useUnassignedSelection,
} from './useUnassignedSelection'

export type { ManualIdentityNames } from './useUnassignedSelection'

/** One screenful of tiles; the tab pages the rest. */
export const UNASSIGNED_PAGE_SIZE = 24

export const unassignedFacesKeys = {
  all: (caseId: string) => ['case', caseId, 'unassigned-faces'] as const,
  page: (caseId: string, page: number) => [...unassignedFacesKeys.all(caseId), page] as const,
}

/** Every face the ticked cards stand for: a cluster's members, a photo's face itself. */
export function facesOfCards(
  selected: readonly string[],
  members: ReadonlyMap<string, readonly string[]>
): string[] {
  const out: string[] = []
  for (const cardId of selected) {
    for (const faceId of members.get(cardId) ?? [cardId]) {
      if (!out.includes(faceId)) out.push(faceId)
    }
  }
  return out
}

export interface UseUnassignedFacesResult
  extends UnassignedSelection<UnassignedFacesPage, ManualIdentity> {
  faces: UnassignedFace[]
}

export function useUnassignedFaces(caseId: string): UseUnassignedFacesResult {
  // No read while AFD is off (ADR-0051); the writes are reachable only from its tab.
  const readCaseId = useFaceReadId(caseId) ?? ''
  // The members of every card seen so far: a tick survives a page change, and the write
  // must still expand it once that page's rows are gone from the cache.
  const members = useRef(new Map<string, readonly string[]>())
  // A write moves faces out of the tab and into an identity, so both the tab
  // and everything face matching reads are stale afterwards.
  const invalidate = useCallback(
    (queryClient: QueryClient) => {
      void queryClient.invalidateQueries({ queryKey: unassignedFacesKeys.all(caseId) })
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'identities'] })
      invalidateFaceMatching(queryClient, caseId)
    },
    [caseId]
  )
  const selection = useUnassignedSelection<UnassignedFacesPage, ManualIdentity>({
    queryKey: page => unassignedFacesKeys.page(readCaseId, page),
    fetchPage: (page, pageSize) => identitiesApi.unassignedFaces(readCaseId, { page, pageSize }),
    total: page => page.total,
    pageSize: UNASSIGNED_PAGE_SIZE,
    enabled: Boolean(readCaseId),
    create: (names: ManualIdentityNames, selected) =>
      identitiesApi.createFromFaces(caseId, {
        ...names,
        face_ids: facesOfCards(selected, members.current),
      }),
    assign: (identityId, selected) =>
      identitiesApi.assignFaces(caseId, identityId, facesOfCards(selected, members.current)),
    invalidate,
  })
  const faces = selection.data?.faces ?? []
  useEffect(() => {
    for (const face of faces) {
      if (face.face_ids && face.face_ids.length > 0)
        members.current.set(face.face_id, face.face_ids)
    }
  }, [faces])
  return { ...selection, faces }
}
